<template>
  <div ref="rootRef" class="ui-dropdown">
    <button
      ref="triggerRef"
      type="button"
      class="ui-button ui-button--secondary ui-dropdown__trigger"
      :class="{ 'ui-button--compact': compact, 'ui-button--icon': iconOnly }"
      :title="iconOnly ? label : undefined"
      :aria-label="iconOnly ? label : undefined"
      aria-haspopup="menu"
      :aria-expanded="isOpen"
      :aria-controls="isOpen ? menuId : undefined"
      :disabled="disabled"
      @click="onTriggerClick"
      @keydown="onTriggerKeydown"
    >
      <LoaderCircle v-if="loading" class="ui-button__icon ui-spin" :size="18" aria-hidden="true" />
      <component :is="icon" v-else class="ui-button__icon" :size="18" aria-hidden="true" />

      <template v-if="!iconOnly">
        <span class="ui-button__label">{{ label }}</span>

        <ChevronDown
          class="ui-dropdown__chevron"
          :size="16"
          :stroke-width="2.6"
          aria-hidden="true"
        />
      </template>
    </button>

    <Teleport to="body">
      <Transition name="ui-pop">
        <div
          v-if="isOpen"
          :id="menuId"
          ref="panelRef"
          class="ui-dropdown__menu"
          role="menu"
          :style="panelStyle"
          @keydown="onMenuKeydown"
          @click="onMenuClick"
        >
          <slot />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { nextTick, provide, useId } from "vue";
import type { Component } from "vue";
import { ChevronDown, LoaderCircle } from "lucide-vue-next";
import { useAnchoredPopover } from "@/components/newUi/core/useAnchoredPopover";

const props = withDefaults(
  defineProps<{
    label: string;
    icon: Component;
    disabled?: boolean;
    loading?: boolean;
    compact?: boolean;
    iconOnly?: boolean;
    closeOnContentClick?: boolean;
  }>(),
  {
    disabled: false,
    loading: false,
    compact: false,
    iconOnly: false,
    closeOnContentClick: false,
  },
);

const menuId = `ui-dropdown-menu-${useId()}`;
const { rootRef, triggerRef, panelRef, isOpen, panelStyle, open, close } = useAnchoredPopover({
  alignRight: true,
  maxHeight: 480,
});

const getItems = (): HTMLElement[] =>
  Array.from(
    panelRef.value?.querySelectorAll<HTMLElement>(
      "[role=menuitem]:not(:disabled), [role=combobox]:not(:disabled), .btn:not(:disabled):not(.disabled)",
    ) ?? [],
  );

const focusItem = (index: number): void => {
  const items = getItems();

  items[Math.max(0, Math.min(items.length - 1, index))]?.focus();
};

const openMenu = async (): Promise<void> => {
  open();

  await nextTick();
  focusItem(0);
};

const closeMenu = (): void => {
  close();
  triggerRef.value?.focus();
};

provide("uiDropdownClose", closeMenu);

const onMenuClick = (event: MouseEvent): void => {
  if (props.closeOnContentClick && (event.target as Element).closest("button, a")) {
    closeMenu();
  }
};

const onTriggerClick = (): void => {
  if (isOpen.value) {
    close();
  } else {
    openMenu();
  }
};

const onTriggerKeydown = (event: KeyboardEvent): void => {
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    openMenu();
  }

  if (event.key === "Escape" && isOpen.value) {
    event.preventDefault();
    event.stopPropagation();
    close();
  }
};

const onMenuKeydown = (event: KeyboardEvent): void => {
  const items = getItems();
  const currentIndex = items.indexOf(document.activeElement as HTMLElement);

  switch (event.key) {
    case "ArrowDown":
      event.preventDefault();
      focusItem(currentIndex + 1);
      break;
    case "ArrowUp":
      event.preventDefault();
      focusItem(currentIndex - 1);
      break;
    case "Home":
      event.preventDefault();
      focusItem(0);
      break;
    case "End":
      event.preventDefault();
      focusItem(items.length - 1);
      break;
    case "Escape":
      event.preventDefault();
      event.stopPropagation();
      closeMenu();
      break;
    case "Tab":
      closeMenu();
      break;
  }
};
</script>
